'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AdBlockContextValue {
  hasAdBlock: boolean;
  checked: boolean;
}

const AdBlockContext = createContext<AdBlockContextValue>({
  hasAdBlock: false,
  checked: false,
});

/**
 * Check 1: Script load bait (detect network/DNS/browser-level block)
 * Dùng self-hosted file → không phụ thuộc tốc độ mạng đến Google
 */
async function checkByBaitScript(): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;
    const done = (val: boolean) => {
      if (!resolved) { resolved = true; resolve(val); }
    };

    const script = document.createElement('script');
    // Path khớp EasyList pattern: /ads/*.js
    script.src = `/ads/banner.js?_=${Date.now()}`; // cache-bust
    script.onload = () => done(false);  // load được → không bị chặn
    script.onerror = () => done(true);  // lỗi ngay → bị chặn
    document.head.appendChild(script);

    // Timeout dài, chỉ là safety net — không kết luận có adblock
    setTimeout(() => done(false), 5000);
  });
}

/**
 * Check 2: DOM bait (detect extension ẩn element theo CSS selector)
 * Extension như uBlock chặn render element có class adsbygoogle
 */
async function checkByBaitDom(): Promise<boolean> {
  return new Promise((resolve) => {
    const el = document.createElement('div');
    // Dùng nhiều class name phổ biến trong filter list
    el.className = 'adsbox adsbygoogle ad-banner';
    el.innerHTML = '<script src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"></script>'; // cần có content, element rỗng có thể bị collapse tự nhiên
    el.style.cssText = `
      position: absolute;
      width: 1px; height: 1px;
      top: -9999px; left: -9999px;
      opacity: 0; pointer-events: none;
    `;
    document.body.appendChild(el);

    // Đợi browser apply style từ extension
    requestAnimationFrame(() => {
      setTimeout(() => {
        const blocked =
          el.offsetHeight === 0 ||
          el.offsetWidth === 0 ||
          getComputedStyle(el).display === 'none' ||
          getComputedStyle(el).visibility === 'hidden';
        document.body.removeChild(el);
        resolve(blocked);
      }, 200); // extension cần ~100-200ms để inject CSS
    });
  });
}

/**
 * Kết hợp cả 2, chạy song song
 */
export async function detectAdblock(): Promise<boolean> {
  const [scriptBlocked, domBlocked] = await Promise.all([
    checkByBaitScript(),
    checkByBaitDom(),
  ]);

  console.log('AdBlock detection:', { scriptBlocked, domBlocked });

  // Chỉ kết luận có adblock khi ít nhất 1 check chắc chắn
  return scriptBlocked || domBlocked;
}

export function AdBlockProvider({ children }: { children: ReactNode }) {
  const [hasAdBlock, setHasAdBlock] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    detectAdblock().then((blocked) => {
      setHasAdBlock(blocked);
      setChecked(true);
    });
  }, []);

  return (
    <AdBlockContext.Provider value={{ hasAdBlock, checked }}>
      {children}
    </AdBlockContext.Provider>
  );
}

export const useAdBlock = () => useContext(AdBlockContext);